import { CUSTOM_EVENT } from '../config';
export default class Filters extends HTMLElement {
    whenLoaded = Promise.all([
        customElements.whenDefined('c-calendar'),
        customElements.whenDefined('calendar-range'),
        customElements.whenDefined('calendar-date')
    ]);

    constructor(m) {
        super(m);

        // UI
        this.$form = this.querySelector('[data-filters-form]');
        this.$reset = this.querySelector('[data-filters-reset]');

        // Binding
        this.onChange = this.onChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onCalendarChange = this.onCalendarChange.bind(this);
        this.onReset = this.onReset.bind(this);

        // Data
        this.baseUrl = this.dataset.baseUrl;

        this.submitOnChange = this.hasAttribute('data-submit-on-change') ?? true;
        this.date = [];
    }

    // =============================================================================
    // Lifecycle methods
    // =============================================================================
    connectedCallback() {
        this.whenLoaded.then(() => {
            requestAnimationFrame(() => {
                this.$calendarRoot = this.querySelector('[data-filters-calendar]');
                this.$calendar = this.$calendarRoot?.$calendar;

                this.bindEvents();
            });
        });
    }

    disconnectedCallback() {
        this.unbindEvents();
    }

    // =============================================================================
    // Events
    // =============================================================================
    bindEvents() {
        if (this.submitOnChange) {
            this.$form.addEventListener('change', this.onChange);
        } else {
            this.$form.addEventListener('submit', this.onSubmit);
        }

        this.$calendar?.addEventListener('change', this.onCalendarChange);
        this.$reset?.addEventListener('click', this.onReset);
    }

    unbindEvents() {
        if (this.submitOnChange) {
            this.$form.removeEventListener('change', this.onChange);
        } else {
            this.$form.removeEventListener('submit', this.onSubmit);
        }

        this.$calendar?.removeEventListener('change', this.onCalendarChange);
        this.$reset?.removeEventListener('click', this.onReset);
    }

    // =============================================================================
    // Callbacks
    // =============================================================================
    onChange(e) {
        this.update();
    }

    onCalendarChange(e) {
        const [year, month, day] = e.target.value.split('-');
        this.date = `/${year}/${parseInt(month)}/${parseInt(day)}`;
        this.update();
    }

    onSubmit(e) {
        e.preventDefault();
        e.stopPropagation();

        this.update();
    }

    onReset(e) {
        e.preventDefault();
        e.stopPropagation();

        // Reset form to default state
        this.$form.reset();
        // Force remove the `checked` attribute on inputs because native reset goes to default values & a `checked` attribute is considered default value
        this.$form
            .querySelectorAll('input[checked]')
            .forEach(($el) => $el.removeAttribute('checked'));
        this.$form
            .querySelectorAll('input[value="all"]')
            .forEach(($el) => $el.setAttribute('checked', ''));
        // Force remove the `selected` attribute on options because native reset goes to default values & a `selected` attribute is considered default value
        this.$form
            .querySelectorAll('option[selected]')
            .forEach(($el) => $el.removeAttribute('selected'));

        this.update();
    }

    update() {
        this.formData = new FormData(this.$form);
        this.updateRules();
        this.updateParams();
        this.applyFilters();
    }

    updateRules() {
        this.rules = {};
        for (const [key, value] of this.formData.entries()) {
            if (key && value.length) {
                // Do not consider empty value
                if (value === 'all') {
                    continue;
                }

                if (!this.rules[key]) {
                    this.rules[key] = [];
                }

                this.rules[key].push(value);
            }
        }
    }

    updateParams() {
        this.urlParams = new URLSearchParams();

        for (let [rule, value] of Object.entries(this.rules)) {
            if (rule == 'orderby' && value == 'default') continue;
            this.urlParams.set(rule, value.join(','));
        }
    }

    applyFilters() {
        if (window.location.search != `?${this.urlParams.toString()}` || this.date != '') {
            let url = this.baseUrl;
            if (this.date != '') url += this.date;
            if (this.urlParams.toString() != '') url += `?${this.urlParams.toString()}`;

            // Dispatch custom event to notify other components
            window.dispatchEvent(
                new CustomEvent(CUSTOM_EVENT.GO_TO, {
                    detail: url
                })
            );
        }
    }
}
